import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Tag, Plus, Trash2, Pencil, MapPin, Phone, Ship, Mail, CalendarClock, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { title: '', description: '', image_url: '', destination: '', price: '', active: true, expires_at: '' };

const PromotionsAdmin = () => {
  const [promos, setPromos] = useState([]);
  const [whatsapp, setWhatsapp] = useState('');
  const [waEdit, setWaEdit] = useState('');
  const [optins, setOptins] = useState({ count: 0, members: [] });
  const [emailForm, setEmailForm] = useState({ host: 'smtps.aruba.it', port: 465, user: '', sender: '', password: '' });
  const [emailConfigured, setEmailConfigured] = useState(false);
  const [sendingNews, setSendingNews] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/community-promotions`);
      setPromos(data.promotions);
      setWhatsapp(data.whatsapp);
      setWaEdit(data.whatsapp);
    } catch (e) {
      toast.error('Errore caricamento');
    }
    try {
      const { data } = await axios.get(`${API}/admin/community-marketing-optins`);
      setOptins(data);
    } catch (e) {}
    try {
      const { data } = await axios.get(`${API}/admin/community-email-settings`);
      setEmailForm((f) => ({ ...f, host: data.host, port: data.port, user: data.user, sender: data.sender, password: '' }));
      setEmailConfigured(data.configured);
    } catch (e) {}
  };

  const saveEmail = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/community-email-settings`, emailForm);
      setEmailConfigured(data.configured);
      setEmailForm((f) => ({ ...f, password: '' }));
      toast.success('Impostazioni email salvate');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const sendNewsletter = async () => {
    if (!window.confirm(`Inviare la newsletter con le offerte attive a ${optins.count} destinatari?`)) return;
    setSendingNews(true);
    try {
      const { data } = await axios.post(`${API}/admin/community-newsletter`);
      toast.success(`Newsletter inviata: ${data.sent} inviate, ${data.failed} fallite`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invio non riuscito');
    } finally {
      setSendingNews(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ title: p.title, description: p.description, image_url: p.image_url, destination: p.destination, price: p.price, active: p.active, expires_at: p.expires_at || '' });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error('Inserisci un titolo');
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/admin/community-promotions/${editing}`, form);
      } else {
        await axios.post(`${API}/admin/community-promotions`, form);
      }
      toast.success('Promozione salvata');
      setOpen(false);
      load();
    } catch (e) {
      toast.error('Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/admin/community-promotions/${id}`);
      setPromos((p) => p.filter((x) => x.id !== id));
      toast.success('Eliminata');
    } catch (e) {
      toast.error('Errore');
    }
  };

  const toggleActive = async (p) => {
    try {
      await axios.put(`${API}/admin/community-promotions/${p.id}`, { ...p, active: !p.active });
      load();
    } catch (e) {
      toast.error('Errore');
    }
  };

  const saveWhatsapp = async () => {
    try {
      const { data } = await axios.put(`${API}/admin/community-agency-whatsapp`, { whatsapp: waEdit });
      setWhatsapp(data.whatsapp);
      toast.success('Numero WhatsApp aggiornato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 py-5 flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/dashboard" className="text-cyan-600 hover:text-cyan-800 text-sm flex items-center gap-1 mb-2" data-testid="promo-admin-back">
              <ArrowLeft className="w-4 h-4" /> Dashboard
            </Link>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Tag className="w-6 h-6 text-orange-500" /> Promozioni Crociere
            </h1>
            <p className="text-slate-500 text-sm mt-1">Gestisci le offerte mostrate nella sezione pubblica della community.</p>
          </div>
          <Button className="bg-orange-500 hover:bg-orange-600" onClick={openNew} data-testid="new-promo-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nuova promozione
          </Button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <Card>
          <CardContent className="p-4 flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[220px]">
              <Label className="flex items-center gap-1.5"><Phone className="w-4 h-4 text-emerald-600" /> Numero WhatsApp agenzia</Label>
              <Input data-testid="whatsapp-input" value={waEdit} onChange={(e) => setWaEdit(e.target.value)} placeholder="+39 375 8270755" />
            </div>
            <Button variant="outline" onClick={saveWhatsapp} data-testid="save-whatsapp-btn">Salva numero</Button>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5"><Mail className="w-4 h-4 text-cyan-600" /> Consensi marketing</Label>
              <span className="bg-cyan-600 text-white text-sm font-bold px-3 py-0.5 rounded-full" data-testid="optins-count">{optins.count}</span>
            </div>
            <p className="text-xs text-slate-500 mb-2">Membri che vogliono essere ricontattati sulle nuove offerte.</p>
            {optins.members.length === 0 ? (
              <p className="text-sm text-slate-400">Nessun consenso ancora.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {optins.members.map((m) => (
                  <a key={m.id} href={`mailto:${m.email}`} className="text-xs bg-slate-100 hover:bg-cyan-50 text-slate-700 px-2.5 py-1 rounded-full" data-testid={`optin-${m.id}`}>
                    {m.name} · {m.email}
                  </a>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-1.5"><Send className="w-4 h-4 text-emerald-600" /> Newsletter offerte</Label>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${emailConfigured ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`} data-testid="email-status">
                {emailConfigured ? 'Email configurata' : 'Da configurare'}
              </span>
            </div>
            <div className="grid sm:grid-cols-2 gap-2">
              <div><Label className="text-xs">Server SMTP</Label><Input data-testid="smtp-host" value={emailForm.host} onChange={(e) => setEmailForm({ ...emailForm, host: e.target.value })} /></div>
              <div><Label className="text-xs">Porta</Label><Input data-testid="smtp-port" type="number" value={emailForm.port} onChange={(e) => setEmailForm({ ...emailForm, port: parseInt(e.target.value) || 465 })} /></div>
              <div><Label className="text-xs">Email (utente)</Label><Input data-testid="smtp-user" value={emailForm.user} onChange={(e) => setEmailForm({ ...emailForm, user: e.target.value })} placeholder="info@tribuinviaggio.com" /></div>
              <div><Label className="text-xs">Mittente (From)</Label><Input data-testid="smtp-sender" value={emailForm.sender} onChange={(e) => setEmailForm({ ...emailForm, sender: e.target.value })} placeholder="info@tribuinviaggio.com" /></div>
              <div className="sm:col-span-2"><Label className="text-xs">Password casella</Label><Input data-testid="smtp-password" type="password" value={emailForm.password} onChange={(e) => setEmailForm({ ...emailForm, password: e.target.value })} placeholder={emailConfigured ? '•••••• (lascia vuoto per non cambiare)' : 'Inserisci la password'} /></div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" onClick={saveEmail} data-testid="save-email-btn">Salva impostazioni email</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={sendNewsletter} disabled={sendingNews || !emailConfigured || optins.count === 0} data-testid="send-newsletter-btn">
                <Send className="w-4 h-4 mr-1.5" /> {sendingNews ? 'Invio in corso...' : `Invia newsletter (${optins.count})`}
              </Button>
            </div>
            <p className="text-xs text-slate-400">La password viene salvata in modo sicuro e non viene mai rimostrata.</p>
          </CardContent>
        </Card>

        {promos.length === 0 ? (
          <div className="text-center py-16 text-slate-400 bg-white rounded-xl border border-dashed">
            <Ship className="w-12 h-12 mx-auto mb-3 opacity-40" /> Nessuna promozione. Creane una!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {promos.map((p) => (
              <Card key={p.id} className="overflow-hidden" data-testid={`admin-promo-${p.id}`}>
                {p.image_url && <img src={p.image_url} alt={p.title} className="w-full h-36 object-cover" />}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-bold text-slate-800">{p.title}</h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.active ? 'Attiva' : 'Nascosta'}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-slate-500">
                    {p.destination && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.destination}</span>}
                    {p.price && <span className="flex items-center gap-1"><Tag className="w-3.5 h-3.5" /> {p.price}</span>}
                    {p.expires_at && <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> scade {p.expires_at}</span>}
                  </div>
                  {p.description && <p className="text-sm text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={p.active} onCheckedChange={() => toggleActive(p)} data-testid={`toggle-promo-${p.id}`} />
                      <span className="text-xs text-slate-500">Visibile</span>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(p)} data-testid={`edit-promo-${p.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50" onClick={() => remove(p.id)} data-testid={`delete-promo-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="promo-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Modifica promozione' : 'Nuova promozione'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Titolo</Label><Input data-testid="promo-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Es. Caraibi 7 notti" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Destinazione</Label><Input data-testid="promo-dest-input" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} placeholder="Es. Caraibi" /></div>
              <div><Label>Prezzo</Label><Input data-testid="promo-price-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Es. da 899€" /></div>
            </div>
            <div><Label>URL immagine</Label><Input data-testid="promo-image-input" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="https://..." /></div>
            <div><Label>Scadenza offerta (facoltativa)</Label><Input data-testid="promo-expiry-input" type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} /></div>
            <div><Label>Descrizione</Label><Textarea data-testid="promo-desc-input" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active-switch" />
              <span className="text-sm text-slate-600">Visibile nella sezione pubblica</span>
            </div>
            <Button className="w-full bg-orange-500 hover:bg-orange-600" onClick={save} disabled={saving} data-testid="save-promo-btn">
              {editing ? 'Salva modifiche' : 'Crea promozione'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PromotionsAdmin;
